import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Injector,
    Input,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { ExtendedRoleRightModel } from '@fi-app/role/extended-role-right.model';
import { AppTextService } from '@fi-shared/app-text';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { IFiDialogResult } from '@fi-shared/fi-dialogs/fi-dialogs';
import {
    FiToggleSwitchLabelAlign,
    FiToggleSwitchSliderColorClass,
} from '@fi-shared/fi-toggle-switch/fi-toggle-switch.component';
import { RoleHelperService } from '../../services/role-helper.service';
import { RoleModel } from '@fi-app/role/role.model';
import { isDefined } from '@fi-shared/utils';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { ILightWeightObject } from '@fi-shared/models';
import { FiCheckbox2AdapterService } from '@fi-shared/fi-checkbox2/fi-checkbox2-adapter.service';
import { IFiCheckBoxChangeEvent, IFiCheckboxItem } from '@fi-shared/fi-checkbox2/fi-checkbox2';
import { AbstractControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import { FiTooltipContentType } from '@fi-shared/fi-tooltip/fi-tooltip-enums';

@Component({
    selector: 'fi-role-extended-rights',
    templateUrl: './role-extended-rights.component.html',
    styleUrls: ['./role-extended-rights.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-role-extended-rights' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: RoleExtendedRightsComponent,
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: RoleExtendedRightsComponent,
            multi: true,
        },
    ],
    standalone: false,
})
export class RoleExtendedRightsComponent
    extends ControlValueAccessorValidatorBase<ExtendedRoleRightModel>
    implements OnInit
{
    @Input() isEditMode: boolean;
    @Input() role: RoleModel;
    @Input() allRoles: ILightWeightObject[];
    @Output() onBackButtonClick = new EventEmitter<void>();

    get extendedRoleRight(): ExtendedRoleRightModel {
        return this.ngModel;
    }

    FiToggleSwitchLabelAlign = FiToggleSwitchLabelAlign;
    extendedShareRoleListData: IFiCheckboxItem;
    extendedAccessRoleListData: IFiCheckboxItem;

    constructor(
        private injector: Injector,
        private appTextService: AppTextService,
        private fiDialogsService: FiDialogsService,
        private roleHelperService: RoleHelperService,
        private detectDevicesService: DetectDevicesService,
        private checkbox2AdapterService: FiCheckbox2AdapterService,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        this.extendedShareRoleListData = {
            header: true,
            _class: 'hidden',
            childItems: this.checkbox2AdapterService.mapLightWeightObjects(this.allRoles),
        };
        this.extendedAccessRoleListData = {
            header: true,
            _class: 'hidden',
            childItems: this.checkbox2AdapterService.mapLightWeightObjects(this.allRoles),
        };
        const baseRoleId = this.extendedRoleRight.BaseRoleId;
        if (isDefined(baseRoleId)) {
            if (this.extendedRoleRight.ExtendedShareRoleIds.indexOf(baseRoleId) === -1) {
                this.extendedRoleRight.ExtendedShareRoleIds.push(baseRoleId);
            }

            /*// https://chainformation.atlassian.net/browse/FI-6261

            if (this.hasManageStaffRights() && this.extendedRoleRight.ExtendedAccessRoleIds.indexOf(baseRoleId) === -1) {
                this.extendedRoleRight.ExtendedAccessRoleIds.push(baseRoleId);
            }*/
        }
    }

    isExtendedShareRoleDisabled($event: IFiCheckBoxChangeEvent): boolean {
        return $event.id === this.extendedRoleRight.BaseRoleId;
    }
    isExtendedAccessRoleDisabled($event: IFiCheckBoxChangeEvent): boolean {
        // https://chainformation.atlassian.net/browse/FI-6261
        return /*$event.id === this.extendedRoleRight.BaseRoleId || */ !this.hasManageStaffRights();
    }

    getManageDisableTitle(): string {
        if (!this.hasManageStaffRights()) return this.appTextService.get('extRights.may_manage_tooltip_no_rights');
    }

    onContentShareToggleChange(value: boolean) {
        if (!value) {
            this.extendedRoleRight.ShareToAll = value;
            this.roleHelperService.setRoleDataChanged(true);
            return;
        }

        this.openConfirmationDialog(this.appTextService.get('share_to_all_confirm'))
            .onYesButtonClick(() => {
                this.extendedRoleRight.ShareToAll = value;
                this.roleHelperService.setRoleDataChanged(true);
            })
            .onNoButtonClick(() => {
                this.extendedRoleRight.ShareToAll = false;
                this.cdr.detectChanges();
            });
    }

    onManageStaffToggleChange(value: boolean) {
        if (!value) {
            this.extendedRoleRight.HasAccessToAll = value;
            this.roleHelperService.setRoleDataChanged(true);
            return;
        }

        this.openConfirmationDialog(this.appTextService.get('extRights.manage_all_confirm'))
            .onYesButtonClick(() => {
                this.extendedRoleRight.HasAccessToAll = value;
                this.roleHelperService.setRoleDataChanged(true);
            })
            .onNoButtonClick(() => {
                this.extendedRoleRight.HasAccessToAll = false;
                this.cdr.detectChanges();
            });
    }

    getToggleValueLabel(value: boolean): string {
        return this.appTextService.get(value ? 'yes' : 'no');
    }

    private openConfirmationDialog(confirmText: string): IFiDialogResult {
        return this.fiDialogsService.confirm({
            headerText: this.appTextService.get('common.please_confirm'),
            messageText: confirmText,
            config: {
                disableClose: true,
            },
        });
    }

    goBackToRoleSettings() {
        this.onBackButtonClick.emit();
    }

    hasManageStaffRights(): boolean {
        return this.roleHelperService.roleHasManageStaffRights(this.role);
    }

    isMobilePhone(): boolean {
        return this.detectDevicesService.isMobilePhone();
    }

    isHasAccessToAllDisabled() {
        return (
            this.roleHelperService.hasAdministrationOnCoreGlobal(this.role.RoleApplicationSettings) ||
            this.roleHelperService.hasNoRightsOnCore(this.role.RoleApplicationSettings)
        );
    }

    doValidate(c: AbstractControl): ValidationErrors {
        return this.validateExtendedRoleRightsFn();
    }

    validateExtendedRoleRightsFn = (): ValidationErrors => {
        return this.roleHelperService.getExtendedRoleRightsValidationErrors(this.role, this.role.ExtendedRoleRight);
    };

    protected readonly FiToggleSwitchSliderColorClass = FiToggleSwitchSliderColorClass;
    protected readonly FiTooltipContentType = FiTooltipContentType;
}
